import { Route } from 'react-router-dom'

import aboutRoutes from '../about/aboutRoutes'
import activityRoutes from '../activity/activityRoutes'
import ConnectConnectivity from './ConnectConnectivity'
import ConnectGeolocation from './ConnectGeolocation'
import ConnectGoogleMap from './ConnectGoogleMap'
import ConnectI18nViz from './ConnectI18nViz'
import ConnectInitLocation from './ConnectInitLocation'
import ConnectLocation from './ConnectLocation'
import ConnectMap from './ConnectMap'
import ConnectNewLocation from './ConnectNewLocation'
import ConnectOverscroll from './ConnectOverscroll'
import ConnectPath from './ConnectPath'
import ConnectReview from './ConnectReview'
import ConnectRootHeight from './ConnectRootHeight'
import ConnectShare from './ConnectShare'
import ConnectTopPanel from './ConnectTopPanel'
import ConnectTypes from './ConnectTypes'
import DisconnectActivity from './DisconnectActivity'
import DisconnectAddReview from './DisconnectAddReview'
import DisconnectInitLocation from './DisconnectInitLocation'
import DisconnectLastViewedListPosition from './DisconnectLastViewedListPosition'
import DisconnectLists from './DisconnectLists'
import DisconnectLocation from './DisconnectLocation'
import DisconnectReview from './DisconnectReview'

const connectRoutes = [
  /*
   * ConnectGoogleMap - /list
   * why: the list page needs a map in Redux
   *
   * action: if showing the page before /map was loaded,
   * briefly visit /map and go back to the target route
   */
  <Route key="connect-list" path={['/map', '/list']}>
    {({ match }) =>
      match && (
        <ConnectGoogleMap
          isTargetRoute={match.path.startsWith('/list')}
          targetRoute="/list"
        />
      )
    }
  </Route>,

  /*
   * ConnectGoogleMap - /filter, see above
   */
  <Route key="connect-filter" path={['/map', '/filters']}>
    {({ match }) =>
      match && (
        <ConnectGoogleMap
          isTargetRoute={match.path.startsWith('/filters')}
          targetRoute="/filters"
        />
      )
    }
  </Route>,

  /*
   * ConnectMap
   * why:
   * - the map needs an initial view in Redux to do a first render
   * - the URL needs to resync if the view is lost from the URLbut the initial view and map are still there (we don't dismount the map on mobile but merely hide it)
   *
   * action:
   * - if no initial view in Redux, get the view from URL or default to our chosen location centred on U of Illinois
   * - if map is available but no URL view, call history.replaceView with what's on the map
   */
  <Route key="connect-map" path={['/map', '/settings', '/locations/init']}>
    <ConnectMap />
  </Route>,
  /*
   * ConnectPath
   * why:
   * - if something else changed the URL, e.g. the back button, a share link on mobile being opened, or history.pushAndChangeView being called, the map needs to get back in sync
   *
   * action:
   * - if the map is present but not in sync with the URL, navigate to the URL's view
   */
  <Route
    key="connect-path"
    path={['/map', '/settings', '/locations', '/reviews']}
  >
    <ConnectPath />
  </Route>,
  /*
   * ConnectNewLocation
   * why: map marker for editing location position and location form need initial position info
   *
   * actions:
   * - use center of URL view to initialise position of new location
   * - if needed, set initial view corresponding to URL view
   */
  <Route key="connect-new-location" path="/locations/new">
    <ConnectNewLocation />
  </Route>,
  /*
   * ConnectInitLocation
   * why:
   * - mobile only URL
   * - map needs to know we're in this state rather than generic map state
   *
   *
   * actions:
   * - if on desktop because of screen resize, go to /locations/new
   * - set a flag in redux
   */
  <Route key="connect-init-location" path={['/locations/init', '/settings']}>
    <ConnectInitLocation />
  </Route>,
  /*
   * ConnectLocation
   * why:
   * - map marker for editing location position and location form need position info
   * - keep track of starting to edit a location
   * - on mobile, we need to center the map on the edited location because the UX involves dragging the marker
   * - on mobile, we need to disable the drawer when arriving from list view
   * - on mobile, the drawer needs the user scrolling up and down
   * - on desktop, clicking location from a settings page should make 'back' go to settings instead of map
   * - location-specific context should reset when going from one location to another
   * - two way binding between URL and initialView like in ConnectMap, but with center of location as fallback
   * - opening a bare URL externally should shift the view in a running app
   * - logged in users get their own location information in the payload
   *
   * actions:
   * - fetch data from backend
   * - if needed, set initial view to location as centre
   * - sync property of being edited from URL routes to Redux
   * - sync property of being viewed in street view from URL routes to Redux
   * - on mobile, center and zoom on edited location
   * - on mobile, disable default overscroll (e.g. a refresh on scroll down in Chrome)
   * - on desktop, reset the fromSettings flag when leaving location
   * - when location id changes, drop fromSettings and userActivity state
   * - if there's no initial view in Redux but there's a location in Redux, set initial view
   * - if there's no view in URL but the initial view / map is present, wait for location to load and set the URL view to location centre
   * - if logging in, refetch location
   */
  <Route
    key="connect-location"
    path={[
      '/locations/:locationId/:nextSegment/:nextNextSegment',
      '/locations/:locationId/:nextSegment',
      '/locations/:locationId',
    ]}
  >
    {({ match }) =>
      match &&
      match.params.locationId !== 'new' &&
      match.params.locationId !== 'init' && (
        <ConnectLocation
          locationId={match.params.locationId}
          isBeingEdited={match.params.nextSegment === 'edit'}
          isBeingEditedPosition={match.params.nextNextSegment === 'position'}
          isSuccessfullyAdded={match.params.nextSegment === 'success'}
        />
      )
    }
  </Route>,
  /*
   * ConnectReview
   * why: edit review form needs backend info
   *
   * actions:
   * - fetch data from backend
   * - if needed, set initial view to reviewed location as centre
   */
  <Route key="connect-review" path="/reviews/:reviewId/edit">
    {({ match }) =>
      match && <ConnectReview reviewId={Number(match.params.reviewId)} />
    }
  </Route>,
  /*
   * ConnectTypes
   * why: app needs reference of known types in the database
   *
   * actions:
   * - fetch data from backend
   * - simplify to keep just the currently selected language
   */
  <Route
    key="connect-types"
    path={[
      '/map',
      '/list',
      '/locations',
      '/reviews',
      '/settings',
      '/changes',
      '/users/:userId/activity',
      '/account/lists',
    ]}
  >
    <ConnectTypes />
  </Route>,
  /*
   * DisconnectLocation
   * why: the location marker needs to go away if user stopped adding or editing location
   *
   * action: clear location in Redux
   */
  <Route key="disconnect-location" path={['/map']}>
    {({ match }) => match && <DisconnectLocation />}
  </Route>,
  /*
   * ConnectOverscroll
   * why: when we ask the user to pan on mobile or scroll a lot, sometimes they accidentally overscroll
   *
   * action: set a style property disabling the default 'refresh page on vertical overscroll' for list, location drawer, and pages with map on it
   */
  <Route
    key="connect-overscroll"
    path={[
      '/locations/:locationId/:nextSegment/:nextNextSegment',
      '/locations/:locationId/:nextSegment',
      '/locations/:locationId',
      '/map',
      '/list',
    ]}
  >
    {({ match }) =>
      match &&
      (!match.params.nextNextSegment ||
        match.params.nextNextSegment === 'position') && <ConnectOverscroll />
    }
  </Route>,

  /*
   * DisconnectReview
   * why: if we start editing the review and then go back to location or to the map, and then open settings, the back button should not take us to the abandoned review
   *
   * action: clear review in Redux
   */
  <Route key="disconnect-review" path={['/map', '/locations']}>
    {({ match }) => match && <DisconnectReview />}
  </Route>,

  /*
   * DisconnectAddReview - from add review form
   * why: if we start adding a review at /locations/:locationId/review and then navigate away, we need to clear the review form state
   *
   * action: clear review in Redux when leaving the context of adding review
   */
  <Route
    key="disconnect-add-review"
    path={['/settings', '/locations/:locationId/review']}
  >
    <DisconnectAddReview />
  </Route>,

  /*
   * DisconnectInitLocation
   * why: need to clear the mobile initialization state when navigating to main views
   *
   * action: clear isBeingInitializedMobile in Redux when on /map or /list
   */
  <Route key="disconnect-init-location" path={['/map', '/list']}>
    <DisconnectInitLocation />
  </Route>,

  /*
   * ConnectI18nViz
   * why: ?i18n_viz=keys and ?i18n_viz=values should trigger translation mode
   *
   * action: monkeypatch the i18n function to add background, title, and click handler
   */
  <Route key="connect-i18n-viz" path="*">
    <ConnectI18nViz />
  </Route>,

  /*
   * ConnectShare
   * why: ?mapType=xyz should update the map type in Redux state
   *
   * action: recognize mapType parameter, update Redux state, and remove parameter from URL
   */
  <Route key="connect-share" path="*">
    <ConnectShare />
  </Route>,

  /*
   * DisconnectActivity
   * why: limit the influence of having browsed activity on the back button
   * we want to go activity -> location -> [back] to activity page, but not with activity -> location -> another location -> [back] or activity -> location -> map -> location -> [back]
   *
   * action: clear when dismounting from activity or locations
   */
  <Route
    key="disconnect-activity"
    path={['/users/:userId/activity', '/locations']}
  >
    <DisconnectActivity />
  </Route>,

  /*
   * DisconnectLastViewedListPosition
   * why: limit the context for list scroll position restoration
   * we want to preserve scroll position when navigating list -> location -> [back] to list,
   * but not when navigating to unrelated pages outside the list/location context
   *
   * action: clear lastViewedListPositionId when dismounting from list or location pages
   */
  <Route key="disconnect-last-viewed-position" path={['/list', '/locations']}>
    <DisconnectLastViewedListPosition />
  </Route>,

  /*
   * DisconnectLists
   * why: limit the context for last viewed list id
   * we want to preserve the last viewed list id when navigating account/lists -> location -> [back] to account/lists,
   * but not when navigating to unrelated pages outside the list/location context
   *
   * action: clear lastViewedListId when dismounting from account/lists or location pages
   */
  <Route key="disconnect-lists" path={['/account/lists', '/locations']}>
    <DisconnectLists />
  </Route>,

  /*
   * ConnectTopPanel
   * why: limit the context for filter and share state
   *
   * action:
   * - when navigating away from map, list, or locations, close the filter and share panels
   * - when going from map to one of location pages, close the panels
   * - when drawer fully opens, close the panels
   */
  <Route key="connect-top-panel" path={['/map', '/list', '/locations']}>
    <ConnectTopPanel />
  </Route>,

  /*
   * ConnectGeolocation
   * why: geolocation tracking needs to respond to requests
   *
   * action: handle geolocation state changes and map positioning
   */
  <Route
    key="connect-geolocation"
    path={['/map', '/locations', '/about/welcome']}
  >
    <ConnectGeolocation />
  </Route>,

  /*
   * ConnectConnectivity
   * why: users need to be warned when they go offline and notified when they come back online
   *
   * action:
   * - show a persistent warning toast when offline
   * - show a success toast when back online
   * - refetch filter counts, map locations, and list locations when back online
   */
  <Route
    key="connect-connectivity"
    path={['/map', '/locations', '/list', '/reviews']}
    render={({ location }) => (
      <ConnectConnectivity isListPage={location.pathname.startsWith('/list')} />
    )}
  />,

  /*
   * ConnectRootHeight
   * why: by default #root is pinned to the viewport (height: 100dvh), which
   * suits the map, list, and settings layouts. Text-heavy pages read better
   * when the document flows and grows naturally (native scrolling, collapsing
   * mobile URL bar).
   *
   * action: while on a text-heavy route, add the `auto-height` class to #root
   * so it uses `height: auto; min-height: 100dvh` (see GlobalStyle); remove it
   * on unmount to restore the default fixed-height layout.
   */
  <Route
    key="connect-root-height"
    path={[
      ...aboutRoutes.map((route) => route.props.path).flat(),
      ...activityRoutes.map((route) => route.props.path).flat(),
    ]}
  >
    {({ match }) => match && <ConnectRootHeight />}
  </Route>,
]

export default connectRoutes
